import { stampOf } from "@/lib/day";
import { requestBugReport } from "@/lib/support/bug-report-request";
import { useMemo, type ReactNode } from "react";
import {
  AppWindow,
  CircleX,
  Clock,
  Download,
  FileText,
  TriangleAlert,
} from "lucide-react";
import type {
  GenerateRunResponse,
  RunTermLock,
  ServedStop,
  StageProgress,
} from "@patent-studio/api-types";
import { Button, buttonChrome } from "@/components/ui/button";
import { IconTile } from "@/components/ui/IconTile";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Note } from "@/components/ui/Note";
import { Panel } from "@/components/ui/Panel";
import { ProgressBar } from "@/components/ui/ProgressBar";
import { Spinner } from "@/components/ui/Spinner";
import { MaybeLink } from "@/v2/layout/MaybeLink";
import { StagePage } from "@/v2/layout/StagePage";
import { findStage, stagePath } from "@/v2/stage-vocabulary";
import { DRAFTING, stageLabel } from "./run-copy";
import { SurveyCard } from "./SurveyCard";
import { ProblemScope, useProblemScope } from "@/lib/problem/problem-scope";

/** Try again on a failed run: start it again, and what the server said if it refused. */
export interface RetryControl {
  onRetry: () => void;
  pending: boolean;
  /** The server's words for a refused start, or null. */
  refusal: string | null;
}

export interface DownloadControl {
  onDownload: () => void;
  pending: boolean;
  failed: boolean;
  /** The wait for the run's zip has run out with no zip. */
  overdue: boolean;
}

const EYEBROW = "Generate Patent Draft";

function Fact({
  icon,
  title,
  detail,
}: {
  icon: ReactNode;
  title: string;
  detail: string;
}) {
  return (
    <Panel as="li" shape="card" pad="snug" className="flex items-start gap-3.5">
      <IconTile tone="brand">{icon}</IconTile>
      <div>
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-[13px] text-muted-foreground">{detail}</p>
      </div>
    </Panel>
  );
}

/** The stage in flight, or the last one reached, and how many are done. */
function progressOf(progress: readonly StageProgress[]) {
  const current =
    progress.find((stage) => stage.state === "running") ??
    [...progress].reverse().find((stage) => stage.state === "done");
  return {
    label: current === undefined ? DRAFTING : stageLabel(current.stage_id),
    done: progress.filter((stage) => stage.state === "done").length,
  };
}

/**
 * When the start locked the terms, how many, and how many were edited from the
 * review, in v0.28's words under the processing message.
 */
function lockLine(lock: RunTermLock): string {
  const edits = lock.edited?.length ?? 0;
  const edited =
    edits === 0
      ? "no edits made from this step"
      : `${String(edits)} edited from this step`;
  return `Terminology locked ${stampOf(lock.locked_at)} · ${String(lock.term_count)} terms · ${edited}`;
}

function Processing({
  run,
  unreachable,
}: {
  run: GenerateRunResponse;
  unreachable: boolean;
}) {
  const progress = progressOf(run.progress);
  return (
    <StagePage
      align="center"
      eyebrow={EYEBROW}
      mark={
        <div className="flex flex-col items-center gap-5">
          <Spinner size="lg">
            <FileText />
          </Spinner>
          <span className="flex items-center gap-1.5 text-brand-mid">
            <MicroLabel className="font-mono text-brand-mid">
              Generating
            </MicroLabel>
            <Spinner size="dots" />
          </span>
        </div>
      }
      title="Your patent draft is being created"
      lede={
        run.state === "queued" ? "Waiting for a free drafting slot…" : undefined
      }
    >
      {run.term_lock !== null && (
        <p className="mt-1.5 text-center text-[12px] text-muted-foreground">
          {lockLine(run.term_lock)}
        </p>
      )}
      {unreachable && (
        <Note tone="warning" className="mx-auto mt-2 max-w-[520px]">
          We cannot reach the server right now. Your draft is still being
          generated.
        </Note>
      )}
      {run.state === "running" && (
        <ProgressBar
          className="mx-auto mt-4 max-w-[420px]"
          value={progress.done}
          max={run.progress.length}
          label={progress.label}
        />
      )}
      <ul className="mx-auto mt-6 flex max-w-[620px] flex-col gap-3 text-left">
        <Fact
          icon={<Clock />}
          title="This can take up to 20 minutes."
          detail="Longer disclosures with many figures sit at the upper end."
        />
        <Fact
          icon={<AppWindow />}
          title="It is safe to close this browser tab."
          detail="Generation continues on our servers whether or not you stay here."
        />
      </ul>
    </StagePage>
  );
}

/**
 * What the zip holds, each file named in bold, as v0.28 lists them. The
 * drawings are named only when the run made them: a matter with no placed
 * figure has none to file.
 */
function Includes({ drawings }: { drawings: boolean }) {
  return (
    <>
      <p className="mt-5 text-[15px] font-bold">Download file includes:</p>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm marker:text-brand-mid">
        <li>
          <strong>The patent draft document</strong> (.docx)
        </li>
        <li>
          <strong>Decision record</strong> (.pdf): How the draft was produced:
          the decisions made and the sources behind them.
        </li>
        <li>
          <strong>Figures log</strong> (.pdf): Every figure in one document:
          components, descriptions and changes made.
        </li>
        {drawings && (
          <li>
            <strong>Drawings</strong> (.pdf): The drawing sheets to file with
            the application, one page per sheet.
          </li>
        )}
      </ul>
    </>
  );
}

/** The one download, large on the card's right, held while the zip is prepared. */
function DownloadAct({
  bundled,
  download,
}: {
  bundled: boolean;
  download: DownloadControl;
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <Button
        variant="brand"
        size="hero"
        held={!bundled}
        onClick={download.onDownload}
        disabled={download.pending}
      >
        {bundled ? <Download /> : <Spinner size="sm" />}
        {!bundled
          ? "Preparing your download…"
          : download.pending
            ? "Preparing download…"
            : "Download Patent Draft (.zip)"}
      </Button>
      {bundled && download.failed && (
        <Note tone="destructive" role="alert">
          The download could not start. Try again.
        </Note>
      )}
      {!bundled && download.overdue && (
        <Note>
          The download is taking longer than usual. Reload this page in a few
          minutes.
        </Note>
      )}
    </div>
  );
}

function Ready({
  taskId,
  bundled,
  drawings,
  download,
}: {
  taskId: string;
  bundled: boolean;
  /** Whether the run made the drawings, which the zip then holds. */
  drawings: boolean;
  download: DownloadControl;
}) {
  return (
    <StagePage
      eyebrow={EYEBROW}
      title="Your patent draft is ready!"
      lede="Your patent draft is available to download below as a .docx file, so you can make any amendments you need. The Decision record and Figures documents are also available as a record of the process that went into generating the draft."
    >
      <Panel
        shape="card"
        pad="roomy"
        cap
        className="mt-6 grid items-center gap-8 md:grid-cols-[minmax(0,1fr)_auto]"
      >
        <div>
          <MicroLabel className="font-mono text-brand-mid">
            Your draft
          </MicroLabel>
          <div className="mt-3 flex items-start gap-3.5">
            <IconTile tone="brand" size="md">
              <FileText />
            </IconTile>
            <div>
              <h2 className="text-[22px] font-bold">Completed draft</h2>
              <p className="max-w-[460px] text-sm text-muted-foreground">
                The finished patent draft, assembled from the information you
                provided across every stage, ready for you to review.
              </p>
            </div>
          </div>
          <Includes drawings={drawings} />
        </div>
        <DownloadAct bundled={bundled} download={download} />
      </Panel>
      <SurveyCard taskId={taskId} />
    </StagePage>
  );
}

function Ended({
  icon,
  title,
  line,
  children,
}: {
  icon: ReactNode;
  title: string;
  line: string;
  children?: ReactNode;
}) {
  return (
    <StagePage
      align="center"
      eyebrow={EYEBROW}
      mark={icon}
      title={title}
      lede={line}
    >
      {children}
    </StagePage>
  );
}

/** What a start the server refused said, under the button that asked. */
function Refusal({ retry }: { retry: RetryControl }) {
  return retry.refusal === null ? null : (
    <Note tone="destructive" role="alert" className="mt-2">
      {retry.refusal}
    </Note>
  );
}

/**
 * A run a check stopped, in the section's own title where the framework gives
 * it one. A section a writer writes stopped on the writer's own output, so the
 * fault is Praxis's and Generate again, which asks afresh, usually clears it.
 * A section code makes from a stage's records stops the same way every time,
 * so the page sends the attorney to that stage instead. Either way it offers
 * a bug report carrying the run's ids, never what the checks found.
 */
function Stopped({
  stop,
  run,
  taskId,
  retry,
}: {
  stop: ServedStop;
  run: GenerateRunResponse;
  taskId: string;
  retry: RetryControl;
}) {
  const section = stop.section_title ?? stop.section_label;
  const stageName = findStage(stop.fix_stage)?.label ?? stop.fix_stage;
  const reportButton = (
    <ReportRun
      run={run}
      about={{
        Section: `${stop.section_label} (${stop.section_id ?? "none"})`,
        Rule: stop.rule_id,
        ...(run.finished_at === null ? {} : { "Stopped at": run.finished_at }),
      }}
    />
  );
  return (
    <Ended
      title="The draft stopped before it finished"
      line={
        stop.rewritable
          ? `Patent Studio couldn't finish writing "${section}". The fault was in Patent Studio's writing, not in your inputs.`
          : `Patent Studio couldn't finish "${section}". It's made from what's in ${stageName}, so generating again gives the same result.`
      }
      icon={
        <IconTile tone="warning" shape="round" size="md">
          <TriangleAlert />
        </IconTile>
      }
    >
      <p className="mx-auto mt-4 max-w-[520px] text-sm text-muted-foreground">
        {stop.rewritable
          ? "Generate again usually completes it. If it stops a second time, let us know and we'll look into it."
          : `Check ${stageName}, then generate again. If it still stops, let us know and we'll look into it.`}
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        {stop.rewritable ? (
          <Button onClick={retry.onRetry} disabled={retry.pending}>
            {retry.pending ? "Starting again…" : "Generate again"}
          </Button>
        ) : (
          <MaybeLink
            to={stagePath(taskId, stop.fix_stage)}
            className={`inline-flex ${buttonChrome({ variant: "primary" })}`}
          >
            {`Go to ${stageName}`}
          </MaybeLink>
        )}
        {reportButton}
      </div>
      <Refusal retry={retry} />
    </Ended>
  );
}

/**
 * Report this problem on a run that did not finish: the run and the draft, from the scope
 * `RunView` sets, then what it is about, by default the failure's category.
 */
function ReportRun({
  run,
  about = { Failure: run.failure ?? "none" },
}: {
  run: GenerateRunResponse;
  about?: Readonly<Record<string, string>>;
}) {
  const scope = useProblemScope();
  return (
    <Button
      variant="secondary"
      onClick={() => {
        requestBugReport({ ...scope, ...about });
      }}
    >
      Report this problem
    </Button>
  );
}

function Failed({
  run,
  retry,
}: {
  run: GenerateRunResponse;
  retry: RetryControl;
}) {
  return (
    <Ended
      title="The draft could not be generated"
      line="Something went wrong on our side. Your inputs are kept. Try again."
      icon={
        <IconTile tone="destructive" shape="round" size="md">
          <CircleX />
        </IconTile>
      }
    >
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <Button onClick={retry.onRetry} disabled={retry.pending}>
          {retry.pending ? "Starting again…" : "Try again"}
        </Button>
        <ReportRun run={run} />
      </div>
      <Refusal retry={retry} />
    </Ended>
  );
}

/**
 * A run the draft store refused. No input of the attorney's causes it, and
 * every new run fails the same way until the store is set up, so there is no
 * Try again: each try would spend model calls before it fails. Report is the
 * way on, carrying the run's ids.
 */
function NotSaved({ run }: { run: GenerateRunResponse }) {
  return (
    <Ended
      title="Patent Studio couldn't save the draft"
      line="The fault is on our side, not in your inputs, and generating again would fail the same way until we fix it. Report it and we'll look into it. Your inputs are kept."
      icon={
        <IconTile tone="destructive" shape="round" size="md">
          <CircleX />
        </IconTile>
      }
    >
      <div className="mt-6">
        <ReportRun run={run} />
      </div>
    </Ended>
  );
}

/**
 * One draft run as the attorney reads it: being created, ready to download,
 * stopped by a check, or failed. The failure's own words are the operator's
 * and never reach the screen.
 */
export function RunView(props: RunViewProps) {
  const { run, taskId } = props;
  // Every report on the run's pages carries the draft and the run.
  const ids = useMemo(
    () => ({ Draft: taskId, "Draft run": run.run_id }),
    [taskId, run.run_id],
  );
  return (
    <ProblemScope ids={ids}>
      <RunState {...props} />
    </ProblemScope>
  );
}

interface RunViewProps {
  run: GenerateRunResponse;
  taskId: string;
  download: DownloadControl;
  retry: RetryControl;
  /** Polls have failed in a row while the run is in flight. */
  unreachable?: boolean;
}

function RunState({
  run,
  taskId,
  download,
  retry,
  unreachable = false,
}: RunViewProps) {
  switch (run.state) {
    case "queued":
    case "running":
      return <Processing run={run} unreachable={unreachable} />;
    case "ready":
      return (
        <Ready
          taskId={taskId}
          bundled={run.artifacts.some((file) => file.kind === "bundle")}
          drawings={run.artifacts.some((file) => file.kind === "drawings")}
          download={download}
        />
      );
    case "stopped":
      return run.stop === null ? (
        <Failed run={run} retry={retry} />
      ) : (
        <Stopped stop={run.stop} run={run} taskId={taskId} retry={retry} />
      );
    case "failed":
      return run.failure === "store_unavailable" ? (
        <NotSaved run={run} />
      ) : (
        <Failed run={run} retry={retry} />
      );
  }
}
